import React from 'react';
import { Form } from 'react-bootstrap';
import { EXAMEN_ESTOMATOGNATICO_TABLA_BASE } from './fichaOdontologicaLecturaHelpers';
import MspSelectionBox from './MspSelectionBox';

function ensureObj(val) {
  return val && typeof val === 'object' && !Array.isArray(val) ? val : {};
}

/**
 * Examen estomatognático: cuadros MSP con × para marcar regiones con hallazgo y descripción debajo.
 */
export default function ExamenEstomatognaticoGrid({
  form,
  setForm,
  readOnly = false,
  extras = [],
}) {
  const marcas = ensureObj(form.examenEstomatognaticoMarcas);
  const detalles = ensureObj(form.examenEstomatognaticoDetalles);
  const exMarcas = ensureObj(form.examenOdontoExtrasMarcas);
  const exDetalles = ensureObj(form.examenOdontoExtrasValores);

  const patchMarca = (mapKey, rowKey, valor) => {
    setForm((f) => {
      const next = {
        ...f,
        [mapKey]: { ...ensureObj(f[mapKey]), [rowKey]: valor },
      };
      if (mapKey === 'examenEstomatognaticoMarcas') {
        const det = ensureObj(f.examenEstomatognaticoDetalles)[rowKey] || '';
        next[rowKey] = valor ? det : '';
        if (!valor) {
          next.examenEstomatognaticoDetalles = { ...ensureObj(f.examenEstomatognaticoDetalles), [rowKey]: '' };
        }
      }
      if (!valor && mapKey === 'examenOdontoExtrasMarcas') {
        next.examenOdontoExtrasValores = { ...ensureObj(f.examenOdontoExtrasValores), [rowKey]: '' };
      }
      return next;
    });
  };

  const patchDetalle = (rowKey, valor, legacyKey) => {
    setForm((f) => {
      const marcasF = ensureObj(f.examenEstomatognaticoMarcas);
      const next = {
        ...f,
        examenEstomatognaticoDetalles: { ...ensureObj(f.examenEstomatognaticoDetalles), [rowKey]: valor },
      };
      if (legacyKey && marcasF[rowKey]) next[legacyKey] = valor;
      return next;
    });
  };

  const patchDetalleExtra = (exId, valor) => {
    setForm((f) => ({
      ...f,
      examenOdontoExtrasValores: { ...ensureObj(f.examenOdontoExtrasValores), [exId]: valor },
    }));
  };

  const items = [
    ...EXAMEN_ESTOMATOGNATICO_TABLA_BASE.map((r) => ({ ...r, kind: 'base' })),
    ...extras.map((ex) => ({ key: ex.id, label: ex.label, kind: 'extra' })),
  ];

  const hallazgosCount = items.filter((item) =>
    item.kind === 'extra' ? Boolean(exMarcas[item.key]) : Boolean(marcas[item.key])
  ).length;

  return (
    <div className="examen-estoma-panel">
      <div className="examen-estoma-panel__hdr">
        <div className="examen-estoma-panel__hdr-text">
          <i className="bi bi-body-text" aria-hidden />
          <div>
            <strong>Examen sistema estomatognático</strong>
            <p>
              Marque con <strong>×</strong> las regiones con hallazgo o alteración e indique la descripción debajo.
            </p>
          </div>
        </div>
        {!readOnly && (
          <span className="examen-estoma-panel__count" title="Regiones marcadas con hallazgo">
            {hallazgosCount} / {items.length}
          </span>
        )}
      </div>

      <div className="examen-estoma-legend" role="note">
        <span className="examen-estoma-legend__item">
          <span className="examen-estoma-legend__box" aria-hidden />
          Sin hallazgo
        </span>
        <span className="examen-estoma-legend__item">
          <span className="examen-estoma-legend__box examen-estoma-legend__box--on" aria-hidden>
            ×
          </span>
          Con hallazgo — describa la anomalía
        </span>
      </div>

      <div className="examen-estoma-grid" role="list">
        {items.map((item) => {
          const isExtra = item.kind === 'extra';
          const marcado = isExtra ? Boolean(exMarcas[item.key]) : Boolean(marcas[item.key]);
          const detalle = isExtra ? exDetalles[item.key] ?? '' : detalles[item.key] ?? '';

          return (
            <article
              key={item.key}
              className={`examen-estoma-card${marcado ? ' examen-estoma-card--on' : ''}`}
              role="listitem"
            >
              <div className="examen-estoma-card__head">
                <span className="examen-estoma-card__label">{item.label}</span>
                <MspSelectionBox
                  variant="inline"
                  selected={marcado}
                  readOnly={readOnly}
                  onToggle={() =>
                    patchMarca(
                      isExtra ? 'examenOdontoExtrasMarcas' : 'examenEstomatognaticoMarcas',
                      item.key,
                      !marcado
                    )
                  }
                  ariaLabel={
                    marcado
                      ? `Quitar hallazgo en ${item.label}`
                      : `Marcar hallazgo en ${item.label}`
                  }
                />
              </div>

              {marcado ? (
                readOnly ? (
                  <p className="examen-estoma-card__detalle examen-estoma-card__detalle--read">
                    {(detalle || '').trim() || 'Hallazgo sin describir'}
                  </p>
                ) : (
                  <Form.Control
                    size="sm"
                    className="examen-estoma-card__input"
                    value={detalle}
                    onChange={(e) =>
                      isExtra
                        ? patchDetalleExtra(item.key, e.target.value)
                        : patchDetalle(item.key, e.target.value, item.key)
                    }
                    placeholder="Anomalía, alteración o hallazgo clínico…"
                    aria-label={`Descripción: ${item.label}`}
                  />
                )
              ) : (
                <p className="examen-estoma-card__hint">
                  {readOnly ? 'Sin hallazgo' : 'Pulse el cuadro si hay alteración'}
                </p>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
